import { useEffect, useState, useRef, useCallback } from 'react';

/**
 * Hook que retorna versão "atrasada" de um valor.
 *
 * Útil pra evitar disparar busca/filtro a cada tecla — espera o usuário parar
 * de digitar por X ms antes de atualizar o valor consumido.
 *
 * Exemplo:
 *   const [search, setSearch] = useState('');
 *   const debouncedSearch = useDebounce(search, 300);
 *
 *   // useEffect só roda quando o usuário para de digitar por 300ms
 *   useEffect(() => {
 *     fetchResults(debouncedSearch);
 *   }, [debouncedSearch]);
 */
export function useDebounce<T>(value: T, delay: number = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

/**
 * Versão callback do debounce — embrulha uma função pra ser chamada com delay.
 *
 * const debouncedSave = useDebouncedCallback(saveDraft, 500);
 * <input onChange={(e) => debouncedSave(e.target.value)} />
 *
 * O callback retornado é estável (referência não muda entre renders),
 * então é seguro passar pra useEffect deps.
 */
export function useDebouncedCallback<TArgs extends unknown[]>(
  callback: (...args: TArgs) => void,
  delay: number = 300
): (...args: TArgs) => void {
  const callbackRef = useRef(callback);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mantém a referência do callback sempre atualizada sem recriar a função
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Limpa timer pendente ao desmontar
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return useCallback(
    (...args: TArgs) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    },
    [delay]
  );
}
